import { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import type { Profile } from '../types';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

export function usePresence(user: User | null, profile: Profile | null) {
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user || !profile || !isSupabaseConfigured) {
      setOnlineIds(new Set());
      return;
    }

    const client = getSupabase();
    const channel = client.channel('relay-online-users', {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState<{ user_id: string }>();
      setOnlineIds(new Set(Object.keys(state)));
    });

    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          user_id: user.id,
          display_name: profile.display_name,
          online_at: new Date().toISOString(),
        });

        await client
          .from('profiles')
          .update({ status: 'online', last_seen: new Date().toISOString() })
          .eq('id', user.id);
      }
    });

    const heartbeat = window.setInterval(() => {
      client
        .from('profiles')
        .update({ status: 'online', last_seen: new Date().toISOString() })
        .eq('id', user.id)
        .then();
    }, 60_000);

    return () => {
      window.clearInterval(heartbeat);
      client
        .from('profiles')
        .update({ status: 'offline', last_seen: new Date().toISOString() })
        .eq('id', user.id)
        .then();
      client.removeChannel(channel);
    };
  }, [profile, user]);

  return onlineIds;
}

